import React, { useEffect, useState } from 'react';
import { FaSitemap } from '@react-icons/all-files/fa/FaSitemap';
import { FaClock } from '@react-icons/all-files/fa/FaClock';
import { FaLayerGroup } from '@react-icons/all-files/fa/FaLayerGroup';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';
import { FaTimesCircle } from '@react-icons/all-files/fa/FaTimesCircle';
import { FaBuilding } from '@react-icons/all-files/fa/FaBuilding';
import { FaUser } from '@react-icons/all-files/fa/FaUser';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { TabPanelContainer, TabPanel, useTabPanel } from '../../../../commons/TabPanel';
import WorkflowDesignerProperties from './diseno-node-properties';
import WorkflowEdgeProperties from './diseno-edge-properties';
import UsuariosFilter from '../../../../commons/UsuariosFilter';
import {
    resolveEmpresaDisenoEmpresa,
    resolveEmpresaDisenoPersonalizado,
    resolveEmpresaDisenoWorkflow,
    resolveEmpresaDisenoWorkflowEmpresa,
} from './workflow-diseno-context';
import { formatWorkflowPeriodicidadLabel } from '../../../../shared/workflow-periodicidad';

// ── Props ─────────────────────────────────────────────────────────────────

interface WorkflowDisenoTabPageProps {
    initialTabItem: string;
    selectedNode: any;
    selectedEdge: any;
    updateNode: (node: any) => void;
    removeNode: (node: any) => void;
    removeEdge: (edge: any) => void;
    addNodeFromSelected: (id: string) => void;
    permitirEliminarNodo?: boolean;
    permitirSeleccionarUsuario?: boolean;
    mostrarConfigActividad?: boolean;
    editarTipoNodo?: boolean;
    editarActividad?: ((node: any) => void) | boolean;
    permitirAgregarHijo?: boolean;
    saveWorkflow: () => void;
    volver: () => void;
    /** Optional workflow entity to display in the Workflow tab */
    workflow?: any;
    /** Optional empresa entity to display in the Empresa tab */
    empresa?: any;
    disenoPersonalizado?: boolean;
    /** Optional full diseno entity from the workflow-empresa path.
     *  Provides workflow info (via profaseWorkflowEmpresa) for the Workflow tab
     *  and empresa info for the Empresa tab. */
    empresaDiseno?: any;
    /** Initial list of usuarios assigned to the workflow empresa */
    usuariosAsignados?: any[];
    /** Called whenever the usuario list changes */
    onUsuarioChange?: (usuarios: any[]) => void;
}

// ── Auto-switcher (must be inside TabPanelContainer) ──────────────────────

function TabAutoSwitch({ selectedNode, selectedEdge }: { selectedNode: any; selectedEdge: any }) {
    const { setActiveTab } = useTabPanel();
    useEffect(() => { if (selectedNode) setActiveTab('nodo'); }, [selectedNode]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (selectedEdge) setActiveTab('transicion'); }, [selectedEdge]); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
}

// ── Component ─────────────────────────────────────────────────────────────

export default function WorkflowDisenoTabPage({
    initialTabItem,
    selectedNode,
    selectedEdge,
    updateNode,
    removeNode,
    removeEdge,
    addNodeFromSelected,
    permitirEliminarNodo,
    permitirSeleccionarUsuario,
    mostrarConfigActividad = true,
    editarTipoNodo,
    editarActividad,
    permitirAgregarHijo,
    workflow,
    empresa,
    disenoPersonalizado,
    empresaDiseno,
    usuariosAsignados,
    onUsuarioChange,
}: WorkflowDisenoTabPageProps) {
    const resolvedWorkflow = workflow ?? resolveEmpresaDisenoWorkflow(empresaDiseno);
    const resolvedEmpresa = empresa ?? resolveEmpresaDisenoEmpresa(empresaDiseno);
    const resolvedDisenoPersonalizado = disenoPersonalizado ?? resolveEmpresaDisenoPersonalizado(empresaDiseno);
    const showEmpresaTab = !!(resolvedEmpresa || resolveEmpresaDisenoWorkflowEmpresa(empresaDiseno));
    const [activeTab, setActiveTab] = useState(initialTabItem ?? 'workflow');

    useEffect(() => {
        if (initialTabItem) setActiveTab(initialTabItem);
    }, [initialTabItem]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <TabPanelContainer
                activeTab={activeTab}
                onTabChange={setActiveTab}
                accentColor="#6366f1"
                style={{ flex: 1 }}
            >
                {/* Syncs tab when node/edge is clicked on canvas */}
                <TabAutoSwitch selectedNode={selectedNode} selectedEdge={selectedEdge} />

                <TabPanel id="workflow" label="Workflow">
                    {resolvedWorkflow
                        ? <WorkflowInfo
                            workflow={resolvedWorkflow}
                            disenoPersonalizado={resolvedDisenoPersonalizado}
                        />
                        : <Placeholder text="Información del Workflow" />
                    }
                </TabPanel>

                {showEmpresaTab && (
                    <TabPanel id="empresa" label="Empresa">
                        {resolvedEmpresa
                            ? <EmpresaInfo
                                empresa={resolvedEmpresa}
                                activo={empresaDiseno?.activo}
                            />
                            : <Placeholder text="Información de la Empresa" />
                        }
                    </TabPanel>
                )}

                <TabPanel id="nodo" label="Nodo" selectable={false}>
                    {selectedNode
                        ? <WorkflowDesignerProperties
                            selectedNode={selectedNode}
                            updateNode={updateNode}
                            removeNode={removeNode}
                            addNodeFromSelected={addNodeFromSelected}
                            permitirEliminarNodo={permitirEliminarNodo}
                            editarTipoNodo={editarTipoNodo}
                            editarActividad={editarActividad}
                            permitirAgregarHijo={permitirAgregarHijo}
                            permitirSeleccionarUsuario={permitirSeleccionarUsuario}
                            mostrarConfigActividad={mostrarConfigActividad}
                        />
                        : <Placeholder text="Selecciona un nodo en el canvas" />
                    }
                </TabPanel>

                <TabPanel id="transicion" label="Transición" selectable={false}>
                    {selectedEdge
                        ? <WorkflowEdgeProperties
                            currentEdge={selectedEdge}
                            removeEdge={removeEdge}
                            editarTipoNodo={editarTipoNodo}
                            editarActividad={editarActividad}
                        />
                        : <Placeholder text="Selecciona una transición en el canvas" />
                    }
                </TabPanel>

                {mostrarConfigActividad && resolvedEmpresa && (
                    <TabPanel id="usuario" label="Usuario">
                        <UsuarioTab
                            initialUsuarios={usuariosAsignados}
                            onChange={onUsuarioChange}
                        />
                    </TabPanel>
                )}

            </TabPanelContainer>



        </div>
    );
}

// ── Small helpers ─────────────────────────────────────────────────────────

function WorkflowInfo({ workflow, disenoPersonalizado }: { workflow: any; disenoPersonalizado?: boolean }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
            <InfoRow icon={<FaSitemap />} label="Nombre" value={workflow.nombre} />
            <InfoRow icon={<FaClock />} label="Periodicidad" value={formatWorkflowPeriodicidadLabel(workflow.periodicidad)} />
            <InfoRow icon={<FaLayerGroup />} label="Clase" value={workflow.workflowClase?.nombre} />

            <div style={wi.row}>
                <span style={wi.label}>Por Defecto</span>
                {workflow.porDefecto
                    ? <span style={wi.yes}><FaCheckCircle />Sí</span>
                    : <span style={wi.no}><FaTimesCircle />No</span>
                }
            </div>

            {disenoPersonalizado !== undefined && (
                <div style={wi.row}>
                    <span style={wi.label}>Diseño Personalizado</span>
                    {disenoPersonalizado
                        ? <span style={wi.yes}><FaCheckCircle />Sí</span>
                        : <span style={wi.no}><FaTimesCircle />No</span>
                    }
                </div>
            )}
        </div>
    );
}

function EmpresaInfo({ empresa, activo }: { empresa: any; activo?: boolean }) {
    const isActivo = !!activo;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, padding: '4px 0' }}>
            <InfoRow icon={<FaBuilding />} label="RFC" value={empresa.rfc} />
            <InfoRow icon={<FaBuilding />} label="Razón Social" value={empresa.razonSocial} />
            {empresa.grupoEmpresarial?.alias ? (
                <InfoRow icon={<FaBuilding />} label="Grupo" value={empresa.grupoEmpresarial.alias} />
            ) : null}

            <div style={wi.row}>
                <span style={wi.label}>Activo</span>
                {isActivo
                    ? <span style={wi.yes}><FaCheckCircle />Sí</span>
                    : <span style={wi.no}><FaTimesCircle />No</span>
                }
            </div>
        </div>
    );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value?: any }) {
    return (
        <div style={wi.row}>
            <span style={wi.label}>
                <span style={wi.icon}>{icon}</span>
                {label}
            </span>
            <span style={wi.value}>{value ?? '—'}</span>
        </div>
    );
}

const wi: Record<string, React.CSSProperties> = {
    row: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '6px 0',
        borderBottom: '1px solid var(--crm-border, #e2e8f0)',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--crm-text-muted, #94a3b8)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    } as React.CSSProperties,
    icon: { fontSize: 10, display: 'flex', color: 'var(--crm-text-muted, #94a3b8)' },
    value: { fontSize: 13, fontWeight: 600, color: 'var(--crm-text-primary, #0f172a)' },
    yes: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        color: 'var(--crm-success-text, #16a34a)',
        fontWeight: 600,
        fontSize: 12,
    },
    no: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        color: 'var(--crm-text-muted, #94a3b8)',
        fontSize: 12,
    },
    formRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '8px 0',
        borderBottom: '1px solid var(--crm-border, #e2e8f0)',
    },
    formLabel: {
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--crm-text-muted, #94a3b8)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    } as React.CSSProperties,
    formControl: { display: 'flex', alignItems: 'center', gap: 8 },
};

// ── Usuario tab ───────────────────────────────────────────────────────────────

const DRAG_TYPE = 'application/x-profase-usuario';

function UsuarioTab({ initialUsuarios, onChange }: {
    initialUsuarios?: any[];
    onChange?: (usuarios: any[]) => void;
}) {
    // useState only uses initialUsuarios once; state is owned here after that.
    // Parent can force a full reset by changing the key prop on UsuarioTab.
    const [usuarios, setUsuarios] = useState<any[]>(initialUsuarios ?? []);
    // controls UsuariosFilter — reset to null after each pick so it can accept a new selection
    const [filterValue, setFilterValue] = useState<any>(null);

    const handleSelect = (u: any | null) => {
        if (!u) return;
        setFilterValue(null); // clear filter input immediately
        setUsuarios(prev => {
            if (prev.some(p => p.id === u.id)) return prev; // no duplicates
            const next = [...prev, u];
            onChange?.(next);
            return next;
        });
    };

    const handleRemove = (id: any) => {
        setUsuarios(prev => {
            const next = prev.filter(u => u.id !== id);
            onChange?.(next);
            return next;
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 0' }}>
            <UsuariosFilter
                label="Agregar usuario"
                value={filterValue}
                onChange={handleSelect}
                placeholder="Buscar y agregar…"
            />
            <UsuarioList usuarios={usuarios} onRemove={handleRemove} />
        </div>
    );
}

// ── UsuarioList ───────────────────────────────────────────────────────────────

function UsuarioList({ usuarios, onRemove }: { usuarios: any[]; onRemove: (id: any) => void }) {
    if (usuarios.length === 0) {
        return (
            <div style={ut.empty}>
                <FaUser style={{ fontSize: 22, color: '#cbd5e1', marginBottom: 6 }} />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>Ningún usuario asignado</span>
            </div>
        );
    }
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {usuarios.map(u => (
                <UsuarioCard key={u.id} usuario={u} onRemove={() => onRemove(u.id)} />
            ))}
        </div>
    );
}

// ── UsuarioCard ───────────────────────────────────────────────────────────────

function UsuarioCard({ usuario, onRemove }: { usuario: any; onRemove: () => void }) {
    const [imgError, setImgError] = useState(false);
    const habilitado = usuario.habilitado !== false;
    const initials = `${(usuario.nombre ?? '')[0] ?? ''}${(usuario.apellido ?? '')[0] ?? ''}`.toUpperCase();

    const handleDragStart = (e: React.DragEvent) => {
        e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(usuario));
        e.dataTransfer.effectAllowed = 'copy';
    };

    const avatarProps = {
        draggable: true as const,
        onDragStart: handleDragStart,
        title: 'Arrastra al nodo para asignar',
    };

    return (
        <div style={{
            ...ut.card,
            background: habilitado ? '#fff' : '#f8fafc',
        }}>
            {/* Avatar + info row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                {/* Draggable avatar */}
                {imgError || !usuario.id
                    ? <div {...avatarProps} style={{ ...ut.avatar, background: habilitado ? '#e0e7ff' : '#f1f5f9', color: habilitado ? '#6366f1' : '#94a3b8', fontSize: 14, fontWeight: 700, cursor: 'grab' }}>
                        {initials || <FaUser size={14} />}
                    </div>
                    : <img
                        {...avatarProps}
                        src={`/public/usuario/${usuario.id}/image.png`}
                        alt={`${usuario.nombre} ${usuario.apellido}`}
                        style={{ ...ut.avatar, objectFit: 'cover', cursor: 'grab' }}
                        onError={() => setImgError(true)}
                    />
                }

                {/* Name / sub / badges */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: habilitado ? '#0f172a' : '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {usuario.nombre} {usuario.apellido}
                    </span>
                    {(usuario.usuario || usuario.email) && (
                        <span style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {usuario.usuario ?? usuario.email}
                        </span>
                    )}
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
                        {usuario.esContador && (
                            <span style={ut.contadorBadge}>
                                <input type="checkbox" readOnly checked style={{ margin: 0, accentColor: '#6366f1', width: 10, height: 10 }} />
                                Contador
                            </span>
                        )}
                        {!habilitado && <span style={ut.inactivoBadge}>Inactivo</span>}
                    </div>
                </div>

                {/* Remove button */}
                <button
                    type="button"
                    onClick={onRemove}
                    title="Quitar usuario"
                    style={ut.removeBtn}
                >
                    <FaTimes size={10} />
                </button>
            </div>

            <span style={{ fontSize: 9, color: '#cbd5e1', fontStyle: 'italic', marginTop: 3, display: 'block' }}>
                Arrastra la foto al nodo
            </span>
        </div>
    );
}

const ut: Record<string, React.CSSProperties> = {
    card: {
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '8px 10px',
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 12px',
        border: '1px dashed #e2e8f0',
        borderRadius: 8,
        background: '#f8fafc',
    },
    avatar: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    removeBtn: {
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: '50%',
        color: '#94a3b8',
        background: '#f1f5f9',
        flexShrink: 0,
        transition: 'background 0.12s, color 0.12s',
    },
    contadorBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 9,
        fontWeight: 600,
        color: '#6366f1',
        background: '#eef2ff',
        border: '1px solid #c7d2fe',
        borderRadius: 4,
        padding: '1px 4px',
    },
    inactivoBadge: {
        fontSize: 9,
        fontWeight: 600,
        color: '#94a3b8',
        background: '#f1f5f9',
        border: '1px solid #e2e8f0',
        borderRadius: 4,
        padding: '1px 4px',
    },
};

function Placeholder({ text }: { text: string }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 12px' }}>
            <span style={{ fontSize: 12, color: 'var(--crm-text-muted, #94a3b8)', textAlign: 'center' }}>{text}</span>
        </div>
    );
}

